/* Failed-request universe for the failure drill-down. Deterministic from the
 * resolved query seed and self-consistent with the summary error-rate (both
 * derive the failed count from the same seed + call volume).
 *
 * The window's failures are drawn ONCE at workspace scope and then filtered, so
 * a single failure carries one agent attribution everywhere it is counted. An
 * agent-scoped count derived independently is what made an agent's KPI card
 * ("126 failed") disagree with that agent's row in the workspace breakdown
 * ("78") for the same window — the exact defect OPSS-352 exists to remove. */

import { AGENTS, CONFIG_IDS, MODELS } from "./catalog";
import { Rng, hashString } from "./seed";
import { sessionIdForIndex, shortId } from "./sessions";
import { buildModel } from "./derive";
import type { ResolvedQuery } from "./derive";
import type {
  ErrorAgentRow,
  ErrorBreakdownData,
  ErrorBreakdownRow,
  ErrorRow,
  Page,
} from "../types";
import { UNTAGGED_AGENT } from "../errorInsights";

/** Cap on materialized rows; `total` still reports the real failed count. */
const MAX_MATERIALIZED = 400;

/** Share of failures the data plane never attributed to an agent. Measured at
 *  65% on language-studio (OPSS-352) — the reason the drill-down surfaces an
 *  `(untagged)` row instead of dropping them, so the mock has to produce them. */
const UNTAGGED_SHARE = 0.65;

/** How many rows the BFF returns per breakdown dimension. */
const BREAKDOWN_LIMIT = 50;

interface Archetype {
  status: number;
  /** Served provider, as `gen_ai.provider.name` reports it (never normalized
   *  here — `gcp.gemini` reaches the UI verbatim). */
  provider: string;
  /** Resolved from the closed vocabulary; `(unparsed)` when the upstream
   *  envelope isn't one we recognize. */
  error_type: string;
  /** The redacted, truncated upstream message — null on a parse miss, where the
   *  view emits no message at all rather than falling back to the raw body. */
  error: string | null;
  weight: number;
}

/** Failure archetypes weighted like the measured production mix (30 days of
 *  language-studio traffic): upstream auth failures dominate, then rate limits;
 *  the Gemini 401 slice and the unparsable HTML 404 are small but are the two
 *  cases the drill-down has to render correctly. */
const ARCHETYPES: Archetype[] = [
  {
    status: 401,
    provider: "anthropic",
    error_type: "authentication_error",
    error: "authentication_error: invalid x-api-key",
    weight: 45,
  },
  {
    status: 429,
    provider: "anthropic",
    error_type: "rate_limit_error",
    error:
      "rate_limit_error: number of request tokens has exceeded your per-minute rate limit",
    weight: 24,
  },
  {
    status: 529,
    provider: "anthropic",
    error_type: "overloaded_error",
    error: "overloaded_error: upstream overloaded, please retry",
    weight: 12,
  },
  {
    status: 401,
    provider: "openai",
    error_type: "invalid_request_error",
    error: "invalid_request_error: incorrect API key provided",
    weight: 10,
  },
  {
    status: 401,
    provider: "gcp.gemini",
    error_type: "UNAUTHENTICATED",
    error:
      "Request had invalid authentication credentials. Expected OAuth 2 access token.",
    weight: 6,
  },
  {
    // The residual class: an HTML error page the allowlist refuses to parse, so
    // it carries a type but deliberately no message.
    status: 404,
    provider: "(unknown)",
    error_type: "(unparsed)",
    error: null,
    weight: 3,
  },
];

/** Normalized grouping key for a message — a mirror of the view's
 *  `error_signature` (lowercase, quoted substrings dropped, hex runs and digits
 *  collapsed to `#`, 120 chars), so mock and BFF group failures the same way. */
function signatureOf(message: string | null): string | null {
  if (message === null) return null;

  return message
    .toLowerCase()
    .replace(/"[^"]*"/g, "")
    .replace(/\b[0-9a-f]{8,}\b/g, "#")
    .replace(/\d+/g, "#")
    .slice(0, 120);
}

function pickArchetype(rng: Rng): Archetype {
  const total = ARCHETYPES.reduce((a, x) => a + x.weight, 0);
  let roll = rng.float() * total;
  for (const a of ARCHETYPES) {
    roll -= a.weight;
    if (roll <= 0) return a;
  }
  return ARCHETYPES[ARCHETYPES.length - 1];
}

/** Total failed calls for a scope — a seeded ~1–5% of call volume. Shared by
 *  the summary error-rate and the row list so they always reconcile. */
export function failedCount(
  resolved: ResolvedQuery,
  callCount: number,
): number {
  const rng = new Rng(hashString(`${resolved.seed}#errN`));
  const rate = 0.01 + rng.float() * 0.04;
  return Math.round(callCount * rate);
}

interface Facets {
  archetype: Archetype;
  model: string;
  agentId: string;
}

/** The dimensions the breakdown aggregates on, drawn in the same order the row
 *  builder draws them so a failure's facets are identical whether it was
 *  materialized into a page or only counted. Sharing this is what makes
 *  `/errors/breakdown` reconcile with `/errors` row for row. */
function drawFacets(rng: Rng, resolved: ResolvedQuery): Facets {
  const archetype = pickArchetype(rng);
  const model = rng.pick(
    resolved.models.length ? resolved.models : MODELS.map((m) => m.name),
  );
  // Always drawn at workspace scope (see `eachFailure`): a failure's agent is a
  // property of the failure, never of the query that happens to be reading it.
  const agentId = rng.float() < UNTAGGED_SHARE ? "" : rng.pick(AGENTS).agent_id;
  return { archetype, model, agentId };
}

/** The same window with any agent filter lifted off — the scope the failure
 *  universe is drawn at. Rebuilding the seed with an empty `a` reproduces byte
 *  for byte what `resolveQuery` emits for the unfiltered query, so the draw an
 *  agent-scoped read walks is the very same draw `/summary` counted. */
function workspaceScope(resolved: ResolvedQuery): ResolvedQuery {
  if (resolved.agentIds.length === 0) return resolved;

  const seed = JSON.stringify({ ...JSON.parse(resolved.seed), a: [] });
  return { ...resolved, agentIds: [], seed };
}

/** `buildModel` is pure in the seed, so memoizing keeps an agent-scoped read
 *  from rebuilding the whole window model on every call. */
const windowCallCounts = new Map<string, number>();

function windowCallCount(ws: ResolvedQuery): number {
  const cached = windowCallCounts.get(ws.seed);
  if (cached !== undefined) return cached;

  const count = buildModel(ws).callCount;
  windowCallCounts.set(ws.seed, count);
  return count;
}

function seedFor(resolved: ResolvedQuery, index: number): Rng {
  return new Rng(hashString(`${resolved.seed}#e${index}`));
}

/** Walk EVERY failure in the window, not just the `MAX_MATERIALIZED` ones a
 *  page can hold — a count over the materialized slice would silently cap at
 *  400 and stop agreeing with /summary.
 *
 *  The walk is always over the workspace-scope universe; an agent filter is
 *  applied as a predicate over that one draw rather than seeding a second,
 *  independent one. `index` is the failure's identity within the window, so a
 *  row materialized under an agent filter is the same row the unfiltered list
 *  shows. */
function eachFailure(
  resolved: ResolvedQuery,
  visit: (facets: Facets, index: number) => void,
): void {
  const ws = workspaceScope(resolved);
  const total = failedCount(ws, windowCallCount(ws));
  const wanted = resolved.agentIds;
  for (let i = 0; i < total; i += 1) {
    const facets = drawFacets(seedFor(ws, i), ws);
    // `(untagged)` is a real filter value on the wire — the BFF maps it back to
    // "agent_id IS NULL" — so match on the label the rest of the stack shows,
    // not on the blank it is stored as. Without this the roster's untagged row
    // is the one row nothing can be filtered to.
    const label = facets.agentId || UNTAGGED_AGENT;
    if (wanted.length > 0 && !wanted.includes(label)) continue;

    visit(facets, i);
  }
}

/** Failures attributed to one agent, off the same single draw the workspace
 *  breakdown and the agent-scoped list use — so the agent's KPI card, its own
 *  drill-down and the workspace breakdown's row for it are one number, and
 *  summing every agent (plus `(untagged)`) returns /summary.failed_calls. */
export function failedCountForAgent(
  resolved: ResolvedQuery,
  agentId: string,
): number {
  let count = 0;
  eachFailure(workspaceScope(resolved), (facets) => {
    if ((facets.agentId || UNTAGGED_AGENT) === agentId) count += 1;
  });
  return count;
}

function buildOneError(
  resolved: ResolvedQuery,
  index: number,
  sessionCount: number,
): ErrorRow {
  const rng = seedFor(resolved, index);
  const { archetype: a, model, agentId } = drawFacets(rng, resolved);
  const configId = resolved.configs.length
    ? rng.pick(resolved.configs)
    : rng.pick(CONFIG_IDS);
  const createdMs =
    resolved.fromMs +
    Math.floor(rng.float() * (resolved.toMs - resolved.fromMs));
  const retryable = a.status === 429 || a.status === 529 || a.status >= 502;
  // Point at a real session in the same universe so the "Open session" link
  // resolves.
  const sessionIdx = Math.floor(
    rng.float() * Math.max(1, Math.min(sessionCount, 400)),
  );
  return {
    request_id: shortId(hashString(`${resolved.seed}#eid${index}`)),
    status_code: a.status,
    error: a.error,
    error_type: a.error_type,
    provider: a.provider,
    model,
    // The mock stands in for a fully-deployed BFF, i.e. the view path, which
    // resolves an unattributed call to the '(untagged)' literal in SQL and never
    // emits "". A blank means only "this source cannot attribute" — a state the
    // mock has no way to be in (OPSS-352).
    agent_id: agentId || UNTAGGED_AGENT,
    config_id: configId,
    session_id: sessionIdForIndex(resolved, sessionIdx),
    created_at: new Date(createdMs).toISOString(),
    latency_ms_gateway:
      a.status === 504 ? rng.int(30000, 60000) : rng.int(80, 4000),
    retry_count: retryable ? rng.int(0, 3) : 0,
    failover: retryable && rng.float() < 0.3,
  };
}

export interface ErrorListOpts {
  limit: number;
  cursor?: string;
  /** Facet filters from the drill-down (OPSS-352) — the mock applies them to
   *  the materialized page exactly as the BFF applies them in SQL. */
  status?: number[];
  provider?: string[];
}

type FacetFilter = Pick<ErrorListOpts, "status" | "provider">;

function matches(
  status: number,
  provider: string,
  filter: FacetFilter,
): boolean {
  if (filter.status?.length && !filter.status.includes(status)) return false;

  if (filter.provider?.length && !filter.provider.includes(provider)) {
    return false;
  }

  return true;
}

/** Newest-first page of failed requests, offset-paginated like the session
 *  list (`offset:N` cursors). */
export function buildErrorList(
  resolved: ResolvedQuery,
  opts: ErrorListOpts,
  sessionCount: number,
): Page<ErrorRow> {
  const ws = workspaceScope(resolved);
  // Materialize by walk order (the failure's index in the window), so the cap
  // takes the same first N failures the unfiltered list would, then narrow.
  const indices: number[] = [];
  eachFailure(resolved, (facets, index) => {
    if (indices.length >= MAX_MATERIALIZED) return;

    if (!matches(facets.archetype.status, facets.archetype.provider, opts)) {
      return;
    }

    indices.push(index);
  });
  const rows = indices
    .map((i) => buildOneError(ws, i, sessionCount))
    .sort((a, b) => b.created_at.localeCompare(a.created_at));

  const offset = opts.cursor ? Number(opts.cursor.replace("offset:", "")) : 0;
  const items = rows.slice(offset, offset + opts.limit);
  const nextOffset = offset + opts.limit;
  const next_cursor = nextOffset < rows.length ? `offset:${nextOffset}` : null;
  return {
    items,
    total: countFailures(resolved, opts),
    next_cursor,
  };
}

/** Failures matching the facet filter across the whole window — the number the
 *  pager's "of N" and /summary.failed_calls both have to agree with. */
function countFailures(resolved: ResolvedQuery, filter: FacetFilter): number {
  let total = 0;
  eachFailure(resolved, ({ archetype }) => {
    if (matches(archetype.status, archetype.provider, filter)) total += 1;
  });
  return total;
}

/** Window-wide aggregate of the same failures, by status × provider ×
 *  signature and by agent.
 *
 *  Counted over every failure in the window (not just the `MAX_MATERIALIZED`
 *  rows the list page can hold), and off the same per-index facets, so both
 *  dimensions sum to `total` and `total` is the number `/summary.failed_calls`
 *  reports — the reconciliation the real drill-down is built on. */
export function buildErrorBreakdown(
  resolved: ResolvedQuery,
  filter: FacetFilter,
): ErrorBreakdownData {
  const signatures = new Map<string, ErrorBreakdownRow>();
  const agents = new Map<string, number>();
  let total = 0;

  eachFailure(resolved, ({ archetype: a, agentId }) => {
    if (!matches(a.status, a.provider, filter)) return;

    total += 1;
    const key = `${a.status}|${a.provider}|${a.error_type}`;
    const row = signatures.get(key);
    if (row) {
      row.count += 1;
    } else {
      signatures.set(key, {
        status_code: a.status,
        provider: a.provider,
        error_type: a.error_type,
        error_signature: signatureOf(a.error),
        sample_message: a.error,
        count: 1,
      });
    }

    const label = agentId || UNTAGGED_AGENT;
    agents.set(label, (agents.get(label) ?? 0) + 1);
  });

  const by_signature = [...signatures.values()]
    .sort((a, b) => b.count - a.count)
    .slice(0, BREAKDOWN_LIMIT);
  const by_agent: ErrorAgentRow[] = [...agents.entries()]
    .map(([agent_id, count]) => ({ agent_id, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, BREAKDOWN_LIMIT);

  return { by_signature, by_agent, total };
}
